/**
 * "The server just changed under you" — a tick per tool, for mounted widgets.
 *
 * A widget fetches on mount. When an agent turn then CHANGES the thing it is showing, nothing
 * tells it: props are unchanged, so no effect re-runs and the advisor keeps reading the old
 * value. Live on 2026-08-24: asked for the email in English, `draft_email` wrote a new draft,
 * the reply said "the draft is now ready for your review in the EmailDraft card" — and the
 * card still showed the French one from four minutes earlier.
 *
 * Deliberately NOT a draft-specific fix: both chunk paths (the dock and the canvas InputDock)
 * already see `tool_end` with the tool's name, so any widget can declare which tools invalidate
 * it and re-read when one of them finishes.
 */

import { create } from 'zustand';

interface WidgetRefreshState {
  /** tool name → how many times it has completed successfully this session. */
  ticks: Record<string, number>;
  bump: (tool: string) => void;
}

export const useWidgetRefreshStore = create<WidgetRefreshState>((set) => ({
  ticks: {},
  bump: (tool) =>
    set((s) => ({ ticks: { ...s.ticks, [tool]: (s.ticks[tool] ?? 0) + 1 } })),
}));

/**
 * A number that changes whenever any of `tools` completes. Put it in a fetching effect's
 * dependency list and the widget re-reads exactly when its data could have changed — no
 * polling, and no refetch on turns that had nothing to do with it.
 */
export function useRefreshTick(...tools: string[]): number {
  return useWidgetRefreshStore((s) => tools.reduce((n, t) => n + (s.ticks[t] ?? 0), 0));
}

/** Called by the chunk handlers when a tool finishes without failing. */
export const notifyToolFinished = (tool: string) => useWidgetRefreshStore.getState().bump(tool);
